import { Tag, ChevronDown, X } from 'lucide-react'
import { cn } from '@/utils'
import { Button, Badge, Popover, PopoverTrigger, PopoverContent } from '@/components/ui'
import type {
  PersonaFilterState,
  PersonaScope,
  PersonaTypeFilter,
} from '@/utils/personaFilters'

interface PersonaFiltersProps {
  filters: PersonaFilterState
  tagUniverse: { tag: string; count: number }[]
  activeCount: number
  setScope: (scope: PersonaScope) => void
  toggleTag: (tag: string) => void
  toggleType: (t: PersonaTypeFilter) => void
  clearAll: () => void
  showVoice: boolean
  showAvatars: boolean
}

const SCOPE_LABELS: { value: PersonaScope; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Mine' },
  { value: 'public', label: 'Public' },
  { value: 'shared', label: 'Shared with me' },
]

export function PersonaFilters({
  filters,
  tagUniverse,
  activeCount,
  setScope,
  toggleTag,
  toggleType,
  clearAll,
  showVoice,
  showAvatars,
}: PersonaFiltersProps) {
  const typeOptions: { value: PersonaTypeFilter; label: string }[] = [
    { value: 'chat', label: 'Chat' },
    ...(showVoice ? [{ value: 'voice' as const, label: 'Voice' }] : []),
    ...(showAvatars ? [{ value: 'avatar' as const, label: 'Avatar' }] : []),
    { value: 'managed', label: 'Managed' },
  ]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Scope segmented control */}
        <div className="inline-flex rounded-lg border border-border p-0.5">
          {SCOPE_LABELS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setScope(s.value)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition-colors',
                filters.scope === s.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Tag multi-select */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="secondary" size="sm" leftIcon={<Tag className="w-4 h-4" />}>
              Tags
              {filters.tags.length > 0 && (
                <span className="ml-1.5 rounded-full bg-primary text-primary-foreground text-xs px-1.5">
                  {filters.tags.length}
                </span>
              )}
              <ChevronDown className="w-4 h-4 ml-1" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="max-h-72 overflow-auto">
            {tagUniverse.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tags yet.</p>
            ) : (
              <div className="space-y-1">
                {tagUniverse.map(({ tag, count }) => (
                  <label
                    key={tag}
                    className="flex items-center gap-2 text-sm cursor-pointer px-1 py-1 rounded-sm hover:bg-accent"
                  >
                    <input
                      type="checkbox"
                      checked={filters.tags.includes(tag)}
                      onChange={() => toggleTag(tag)}
                      className="w-4 h-4 rounded-sm border-border text-primary focus:ring-ring"
                    />
                    <span className="flex-1 truncate">{tag}</span>
                    <span className="text-xs text-muted-foreground">{count}</span>
                  </label>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>

        {/* Type filters */}
        <div className="flex flex-wrap gap-1">
          {typeOptions.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => toggleType(t.value)}
              className={cn(
                'px-2.5 py-1 text-xs rounded-full border transition-colors',
                filters.types.includes(t.value)
                  ? 'bg-primary/10 border-primary/40 text-primary'
                  : 'border-border text-muted-foreground hover:bg-muted',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAll}
            leftIcon={<X className="w-3.5 h-3.5" />}
          >
            Clear all
          </Button>
        )}
      </div>

      {/* Active tag chips */}
      {filters.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filters.tags.map((tag) => (
            <Badge key={tag} variant="default" className="flex items-center gap-1">
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag} filter`}
                onClick={() => toggleTag(tag)}
                className="hover:text-destructive"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
